import axios from 'src/common/axiosInstance';
import React, { useEffect, useState } from "react";
import {
  X,
  UserRound,
  Phone,
  Hash,
  Mail,
  Image,
  FileText,
  Save,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  MessageCircleMore,
} from "lucide-react";

const STATUS_OPTIONS = ["Active", "Inactive", "Available", "Offline"];

let rowSeq = 1;
const makeRow = (key = "", value = "") => ({ id: `m${rowSeq++}`, key, value });

const emptyForm = () => ({
  name: "",
  phone_number: "",
  country_code: "",
  email: "",
  wa_id: "",
  status: "Active",
  profile_picture_url: "",
  notes: "",
  metaRows: [],
  memoryFacts: {},
});

const toForm = (contact) => {
  if (!contact) return emptyForm();

  const metadata =
    contact.metadata && typeof contact.metadata === "object" ? contact.metadata : {};

  return {
    name: contact.name || "",
    phone_number: contact.phone_number || "",
    country_code: contact.country_code || "",
    email: contact.email || "",
    wa_id: contact.wa_id || "",
    status: contact.status || "Active",
    profile_picture_url: contact.profile_picture_url || "",
    notes: contact.notes || "",
    metaRows: Object.entries(metadata).map(([k, v]) =>
      makeRow(k, v === null || v === undefined ? "" : String(v)),
    ),
  };
};

const FIELD =
  "h-10 w-full rounded-[10px] border bg-card-bg px-3 text-sm font-medium text-text-primary outline-none transition placeholder:text-text-muted dark:bg-dark-card dark:text-dark-text-primary dark:placeholder:text-dark-text-muted";
const FIELD_OK =
  "border-border-color focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-dark-border dark:focus:border-dark-primary";
const FIELD_BAD = "border-red-300 focus:border-red-400";
const LABEL =
  "mb-1.5 block text-[11px] font-bold text-text-secondary dark:text-dark-text-secondary";

function CreateContactModal({ workspaceId, open = false, loading = false, error = "", contact = null, onClose, onSubmit }) {
  const isEditMode = Boolean(contact);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [memoryFields, setMemoryFields] = useState([]);
  const [memoryError, setMemoryError] = useState('');
  useEffect(() => {let active=true;setMemoryFields([]);setMemoryError('');
    if(open && !contact && workspaceId) axios.get(`/workspaces/${workspaceId}/ai/contact-fields`).then(({data})=>{if(active)setMemoryFields(data.fields || []);}).catch(()=>{if(active)setMemoryError('Shared fields are unavailable. You can still create this contact; add extra details as custom fields.');});
    return()=>{active=false;};
  },[open,contact,workspaceId]);

  useEffect(() => {
    if (!open) return;
    setForm(toForm(contact));
    setErrors({});
  }, [open, contact]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setMetaRow = (id, patch) =>
    setForm((prev) => ({
      ...prev,
      metaRows: prev.metaRows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));

  const addMetaRow = () =>
    setForm((prev) => ({ ...prev, metaRows: [...prev.metaRows, makeRow()] }));

  const removeMetaRow = (id) =>
    setForm((prev) => ({ ...prev, metaRows: prev.metaRows.filter((r) => r.id !== id) }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required.";
    if (!form.phone_number.trim()) next.phone_number = "Phone number is required.";
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim()))
      next.email = "Enter a valid email address.";

    const keys = form.metaRows.map((r) => r.key.trim().toLowerCase()).filter(Boolean);
    if (new Set(keys).size !== keys.length) next.metadata = "Custom field names must be unique.";
    if (form.metaRows.some((r) => r.value.trim() && !r.key.trim()))
      next.metadata = "Give every custom field a name.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || !validate()) return;

    const metadata = {};
    form.metaRows.forEach((r) => {
      const key = r.key.trim();
      const value = r.value.trim();
      if (key && value) metadata[key] = value;
    });

    const payload = {
      name: form.name.trim(),
      phone_number: form.phone_number.trim(),
      country_code: form.country_code.trim() || null,
      email: form.email.trim() || null,
      wa_id: form.wa_id.trim() || null,
      status: form.status,
      profile_picture_url: form.profile_picture_url.trim() || null,
      notes: form.notes.trim() || null,
      metadata,
      ...(!isEditMode && Object.keys(form.memoryFacts || {}).length ? {memory_facts:form.memoryFacts} : {}),
    };

    await onSubmit?.(payload, contact);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-[#0f2b42]/25 backdrop-blur-[4px]"
        onClick={loading ? undefined : onClose}
      />

      <div className="relative z-10 flex max-h-[94vh] w-full max-w-[620px] flex-col overflow-hidden rounded-2xl border border-border-color bg-card-bg shadow-elevated dark:border-dark-border dark:bg-dark-card">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-light px-5 py-4 dark:border-dark-border-light">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className={[
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
                isEditMode
                  ? "border-border-color bg-slate-100 text-text-secondary dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
                  : "border-[#c1e1f6] bg-[#f1f8fe] text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary",
              ].join(" ")}
            >
              {isEditMode ? <Pencil size={16} /> : <UserRound size={17} />}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-bold text-text-primary dark:text-dark-text-primary">
                {isEditMode ? "Update contact" : "Add contact"}
              </h2>
              <p className="truncate text-[11px] text-text-muted dark:text-dark-text-muted">
                {isEditMode
                  ? "Edit details and custom fields"
                  : "Create a new WhatsApp contact"}
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition hover:bg-theme-50 hover:text-primary disabled:opacity-40 dark:text-dark-text-muted dark:hover:bg-dark-soft-blue"
          >
            <X size={16} />
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto">
          {!isEditMode && <section className="mx-5 mt-5 rounded-xl border border-border-color bg-theme-50 p-4"><h3 className="text-sm font-bold">Shared contact memory</h3><p className="my-2 text-xs text-text-muted">Name, email and phone below are reused by bots. Add details already known from Facebook, a form or your team. All connected bots use these same fields.</p>
            {memoryError && <p className="text-xs" role="status">{memoryError}</p>}
            <div className="grid gap-3 sm:grid-cols-2">{memoryFields.filter(f=>!['name','email','phone_number'].includes(f.key)).map(f=><label key={f.key} className={LABEL}>{f.label}<input className={`${FIELD} ${FIELD_OK}`} maxLength={500} value={form.memoryFacts?.[f.key] || ''} onChange={e=>setField('memoryFacts',{...form.memoryFacts,[f.key]:e.target.value})} placeholder={f.description || f.key}/></label>)}</div>
            <p className="mt-2 text-xs text-text-muted">Saved with the contact and available to the bot before its next reply. Review, correct or protect values in the contact’s memory panel.</p>
          </section>}
          {isEditMode && <p className="mx-5 mt-4 text-xs text-text-muted">Shared bot fields are edited in Contact details → Contact memory, where you can also protect manual values.</p>}
          {error ? (
            <div className="mx-5 mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[11px] font-medium leading-5 text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400">
              {error}
            </div>
          ) : null}

          <div className="space-y-6 p-5">
            {/* Basic */}
            <section>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-primary dark:text-dark-primary">
                Basic information
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>
                    Name <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <UserRound size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="John Doe"
                      className={`${FIELD} pl-9 ${errors.name ? FIELD_BAD : FIELD_OK}`}
                    />
                  </div>
                  {errors.name && <p className="mt-1 text-[10px] text-danger">{errors.name}</p>}
                </div>

                <div>
                  <label className={LABEL}>
                    Phone number <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      value={form.phone_number}
                      onChange={(e) => setField("phone_number", e.target.value)}
                      placeholder="+919876543210"
                      className={`${FIELD} pl-9 ${errors.phone_number ? FIELD_BAD : FIELD_OK}`}
                    />
                  </div>
                  {errors.phone_number && (
                    <p className="mt-1 text-[10px] text-danger">{errors.phone_number}</p>
                  )}
                </div>

                <div>
                  <label className={LABEL}>Country code</label>
                  <div className="relative">
                    <Hash size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      value={form.country_code}
                      onChange={(e) => setField("country_code", e.target.value)}
                      placeholder="91"
                      className={`${FIELD} pl-9 ${FIELD_OK}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL}>Email</label>
                  <div className="relative">
                    <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="john@example.com"
                      className={`${FIELD} pl-9 ${errors.email ? FIELD_BAD : FIELD_OK}`}
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-[10px] text-danger">{errors.email}</p>}
                </div>

                <div>
                  <label className={LABEL}>
                    WhatsApp ID <span className="font-normal text-text-muted">(optional)</span>
                  </label>
                  <div className="relative">
                    <MessageCircleMore size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      value={form.wa_id}
                      onChange={(e) => setField("wa_id", e.target.value)}
                      placeholder="919876543210"
                      className={`${FIELD} pl-9 ${FIELD_OK}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL}>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                    className={`${FIELD} ${FIELD_OK}`}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* Profile */}
            <section>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-primary dark:text-dark-primary">
                Profile
              </p>

              <label className={LABEL}>Profile picture URL</label>
              <div className="relative">
                <Image size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="url"
                  value={form.profile_picture_url}
                  onChange={(e) => setField("profile_picture_url", e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                  className={`${FIELD} pl-9 ${FIELD_OK}`}
                />
              </div>

              <label className={`${LABEL} mt-3`}>Notes</label>
              <div className="relative">
                <FileText size={13} className="absolute left-3 top-3 text-text-muted" />
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  placeholder="Interested in automation services"
                  className="w-full resize-none rounded-[10px] border border-border-color bg-card-bg py-2.5 pl-9 pr-3 text-sm font-medium text-text-primary outline-none transition placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-dark-border dark:bg-dark-card dark:text-dark-text-primary dark:focus:border-dark-primary"
                />
              </div>
            </section>

            {/* Custom fields */}
            <section>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary dark:text-dark-primary">
                  Custom fields
                </p>
                <button
                  type="button"
                  onClick={addMetaRow}
                  className="inline-flex items-center gap-1 rounded-lg border border-border-color bg-card-bg px-2 py-1 text-[11px] font-semibold text-primary transition hover:bg-theme-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-primary"
                >
                  <Plus size={12} />
                  Add field
                </button>
              </div>
              <p className="mb-2.5 text-[11px] text-text-muted dark:text-dark-text-muted">
                Anything beyond the fields above — City, Company, Plan… — is stored on
                the contact as metadata.
              </p>

              {form.metaRows.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border-color px-3 py-3 text-[11px] text-text-muted dark:border-dark-border dark:text-dark-text-muted">
                  No custom fields.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.metaRows.map((row) => (
                    <div key={row.id} className="flex items-center gap-2">
                      <input
                        value={row.key}
                        onChange={(e) => setMetaRow(row.id, { key: e.target.value })}
                        placeholder="Field name"
                        className={`${FIELD} ${FIELD_OK} sm:max-w-[180px]`}
                      />
                      <input
                        value={row.value}
                        onChange={(e) => setMetaRow(row.id, { value: e.target.value })}
                        placeholder="Value"
                        className={`${FIELD} ${FIELD_OK} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => removeMetaRow(row.id)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-text-muted transition hover:bg-red-50 hover:text-danger dark:hover:bg-red-950/30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {errors.metadata && (
                <p className="mt-1.5 text-[10px] text-danger">{errors.metadata}</p>
              )}
            </section>
          </div>

          {/* FOOTER */}
          <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border-light bg-card-bg/95 px-5 py-3 backdrop-blur-sm dark:border-dark-border-light dark:bg-dark-card/95">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="rounded-lg border border-border-color bg-card-bg px-4 py-2 text-xs font-bold text-text-secondary transition hover:bg-theme-50 disabled:opacity-50 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : isEditMode ? (
                <Pencil size={13} />
              ) : (
                <Save size={13} />
              )}
              {loading
                ? isEditMode
                  ? "Updating…"
                  : "Creating…"
                : isEditMode
                  ? "Update contact"
                  : "Create contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateContactModal;
